import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi, AdminReportListItem } from '../../api/admin.api';
import { ReportDetailModal } from './ReportDetailModal';

export const ReportManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('전체');
  const [selectedReason, setSelectedReason] = useState<string>('전체');
  const [currentPage, setCurrentPage] = useState(1);
  const [visibleRows, setVisibleRows] = useState(10);
  const [selectedReport, setSelectedReport] = useState<AdminReportListItem | null>(null);

  const statusTabs = ['전체', '대기중', '처리완료', '기각'];
  const rowsPerPage = 10;

  // API 호출 - 상태별 필터링
  const statusMap: Record<string, string | undefined> = {
    전체: undefined,
    대기중: 'PENDING',
    처리완료: 'RESOLVED',
    기각: 'REJECTED',
  };

  const { data: reports = [], isLoading } = useQuery({
    queryKey: ['adminReports', activeTab],
    queryFn: async () => {
      const response = await adminApi.getAdminReports(statusMap[activeTab]);
      return response.data;
    },
  });

  // 사유별 필터링
  const filteredData =
    selectedReason === '전체'
      ? reports
      : reports.filter((item) => item.reason === selectedReason);

  // 페이지네이션
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  // 모바일 "더 보기"
  const mobileData = filteredData.slice(0, visibleRows);

  const getReasonBadgeStyles = (reason: string) => {
    switch (reason) {
      case 'PROFANITY':
        return 'bg-[#FEE2E2] text-[#991B1B]';
      case 'SPAM':
        return 'bg-[#FED7AA] text-[#9A3412]';
      case 'INAPPROPRIATE':
        return 'bg-[#FEF3C7] text-[#92400E]';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getReasonLabel = (reason: string) => {
    switch (reason) {
      case 'PROFANITY':
        return '욕설';
      case 'SPAM':
        return '스팸';
      case 'INAPPROPRIATE':
        return '부적절한내용';
      default:
        return reason;
    }
  };

  const getStatusBadgeStyles = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'bg-[#FEF3C7] text-[#92400E]';
      case 'RESOLVED':
        return 'bg-[#D1FAE5] text-[#065F46]';
      case 'REJECTED':
        return 'bg-[#F3F4F6] text-[#374151]';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING':
        return '대기중';
      case 'RESOLVED':
        return '처리완료';
      case 'REJECTED':
        return '기각';
      default:
        return status;
    }
  };

  const handleLoadMore = () => {
    setVisibleRows((prev) => Math.min(prev + 10, filteredData.length));
  };

  if (isLoading) {
    return <div className="p-6 text-center">로딩 중...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Header + Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
        <div className="flex flex-col gap-4">
          <h2 className="text-lg lg:text-xl font-bold text-[#030213]">신고 관리</h2>

          {/* Reason Filter */}
          <div className="flex gap-2 overflow-x-auto">
            {['전체', 'PROFANITY', 'SPAM', 'INAPPROPRIATE'].map((reason) => (
              <button
                key={reason}
                onClick={() => setSelectedReason(reason)}
                className={`px-3 py-1.5 text-sm rounded-lg whitespace-nowrap transition-colors ${
                  selectedReason === reason
                    ? 'bg-[#030213] text-white'
                    : 'bg-[#f3f3f5] text-[#6B7280] hover:bg-[#E5E7EB]'
                }`}
              >
                {reason === '전체' ? '전체' : getReasonLabel(reason)}
              </button>
            ))}
          </div>
        </div>

        {/* Status Tabs */}
        <div className="flex gap-2 mt-4 border-b border-[#E5E7EB]">
          {statusTabs.map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                setCurrentPage(1);
              }}
              className={`px-4 py-2 text-sm font-medium transition-colors relative ${
                activeTab === tab
                  ? 'text-[#030213]'
                  : 'text-[#6B7280] hover:text-[#030213]'
              }`}
            >
              {tab}
              {activeTab === tab && (
                <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#030213]" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#f3f3f5]">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                신고된 질문
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                신고자
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                사유
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                상태
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                신고일
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                액션
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-[#E5E7EB]">
            {paginatedData.map((row) => (
              <tr
                key={row.id}
                onClick={() => setSelectedReport(row)}
                className="hover:bg-[#f3f3f5] transition-colors cursor-pointer"
              >
                <td className="px-6 py-4 text-sm text-[#030213] max-w-md truncate">
                  {row.questionTitle}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-[#6B7280]">
                  {row.reporterNickname}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${getReasonBadgeStyles(row.reason)}`}
                  >
                    {getReasonLabel(row.reason)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeStyles(row.status)}`}
                  >
                    {getStatusLabel(row.status)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-[#6B7280]">
                  {new Date(row.regDateTime).toLocaleDateString('ko-KR')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedReport(row);
                    }}
                    className="text-[#030213] hover:underline font-medium"
                  >
                    상세보기
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 p-4 border-t border-[#E5E7EB]">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border border-[#E5E7EB] rounded hover:bg-[#f3f3f5] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              이전
            </button>
            <span className="text-sm text-[#6B7280]">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm border border-[#E5E7EB] rounded hover:bg-[#f3f3f5] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              다음
            </button>
          </div>
        )}
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {mobileData.map((row) => (
          <div
            key={row.id}
            onClick={() => setSelectedReport(row)}
            className="bg-white rounded-lg shadow-sm p-4 cursor-pointer"
          >
            <div className="flex justify-between items-start mb-2">
              <span
                className={`px-2 py-1 text-xs font-medium rounded-full ${getReasonBadgeStyles(row.reason)}`}
              >
                {getReasonLabel(row.reason)}
              </span>
              <span
                className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeStyles(row.status)}`}
              >
                {getStatusLabel(row.status)}
              </span>
            </div>
            <p className="text-sm font-medium text-[#030213] mb-2 line-clamp-2">
              {row.questionTitle}
            </p>
            <div className="flex justify-between items-center text-xs text-[#6B7280]">
              <span>{row.reporterNickname}</span>
              <span>{new Date(row.regDateTime).toLocaleDateString('ko-KR')}</span>
            </div>
          </div>
        ))}

        {/* Load More Button */}
        {visibleRows < filteredData.length && (
          <button
            onClick={handleLoadMore}
            className="w-full py-3 text-sm font-medium text-[#030213] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#f3f3f5] transition-colors"
          >
            더 보기
          </button>
        )}
      </div>

      {/* Modal */}
      {selectedReport && (
        <ReportDetailModal
          report={selectedReport}
          onClose={() => setSelectedReport(null)}
        />
      )}
    </div>
  );
};
